import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API_BASE}/api/tasks-app` });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('task_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    handle401(err);
    return Promise.reject(err);
  }
);

// Shared 401 handler: an expired/invalid session should always drop the user
// back to the Task login instead of leaving them stuck on a broken action.
function handle401(err) {
  if (err.response?.status === 401 && window.location.pathname.startsWith('/tasks') && window.location.pathname !== '/tasks') {
    localStorage.removeItem('task_token');
    localStorage.removeItem('task_user');
    try { sessionStorage.setItem('task_session_expired', '1'); } catch (e) { /* noop */ }
    window.location.href = '/tasks';
  }
}

// Auth
export const taskLogin = (creds) => api.post('/auth/login', creds);
export const taskMe = () => api.get('/auth/me');

// Users
export const getTaskUsers = () => api.get('/users');
export const createTaskUser = (data) => api.post('/users', data);
export const updateTaskUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteTaskUser = (id) => api.delete(`/users/${id}`);
export const resetUserPassword = (id, password) => api.post(`/users/${id}/reset-password`, { password });

// Tasks
export const getTasks = (params) => api.get('/tasks', { params });
export const getTask = (id) => api.get(`/tasks/${id}`);
export const createTask = (data) => api.post('/tasks', data);
export const updateTask = (id, data) => api.put(`/tasks/${id}`, data);
export const deleteTask = (id) => api.delete(`/tasks/${id}`);
export const addTaskComment = (id, text) => api.post(`/tasks/${id}/comment`, { text });
export const getTaskStats = () => api.get('/tasks-stats');

// Firms
export const getFirms = () => api.get('/firms');
export const getAllFirms = () => api.get('/firms/all');
export const createFirm = (data) => api.post('/firms', data);
export const updateFirm = (id, data) => api.put(`/firms/${id}`, data);
export const deleteFirm = (id) => api.delete(`/firms/${id}`);

// Quotations
export const getQuotations = (params) => api.get('/quotations', { params });
export const getQuotation = (id) => api.get(`/quotations/${id}`);
export const createQuotation = (data) => api.post('/quotations', data);
export const updateQuotation = (id, data) => api.put(`/quotations/${id}`, data);
export const deleteQuotation = (id) => api.delete(`/quotations/${id}`);
export const requestQuotationDelete = (id, reason) => api.post(`/quotations/${id}/delete-request`, { reason });
export const getDeleteRequests = () => api.get('/quotations/delete-requests');
export const approveDeleteRequest = (reqId) => api.post(`/quotations/delete-requests/${reqId}/approve`);
export const rejectDeleteRequest = (reqId) => api.post(`/quotations/delete-requests/${reqId}/reject`);
export const getQuotationStats = (params) => api.get('/quotations/stats', { params });
export const getQuotationUserStats = (params) => api.get('/quotations/user-stats', { params });
export const downloadQuotationPdf = (id) => api.get(`/quotations/${id}/pdf`, { responseType: 'blob' });
export const sendQuotation = (id, data) => api.post(`/quotations/${id}/send`, data);
export const getMyCustomers = () => api.get('/quotations/my-customers');
export const getMyProducts = () => api.get('/quotations/my-products');

// Analytics
export const getUserAnalytics = (params) => api.get('/analytics/users', { params });

// Settings
export const getReminderSettings = () => api.get('/settings/reminders');
export const updateReminderSettings = (data) => api.put('/settings/reminders', data);
export const triggerDailyReminders = () => api.post('/send-daily-reminders');

// File upload
export const uploadTaskFile = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export default api;

// ============= FLOW MANAGEMENT SYSTEM =============
const flowApi = axios.create({ baseURL: `${API_BASE}/api/tasks-app/flow` });
flowApi.interceptors.request.use(cfg => {
  const t = localStorage.getItem('task_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
flowApi.interceptors.response.use((res) => res, (err) => { handle401(err); return Promise.reject(err); });

export const getFlowDashboard = (companyId) => flowApi.get('/dashboard-stats', { params: companyId ? { company_id: companyId } : {} });
export const getSalesOrders = (params) => flowApi.get('/sales-orders', { params });
export const getSalesOrder = (id) => flowApi.get(`/sales-orders/${id}`);
export const createSalesOrder = (data) => flowApi.post('/sales-orders', data);
export const getApprovalQueue = (companyId, includeResolved) => flowApi.get('/approval-queue', { params: { ...(companyId ? { company_id: companyId } : {}), ...(includeResolved ? { include_resolved: true } : {}) } });
export const approveOrderItems = (soId, decisions) => flowApi.post(`/sales-orders/${soId}/approve-items`, { decisions });
export const getStoreQueue = (companyId, q) => flowApi.get('/store-queue', { params: { ...(companyId ? { company_id: companyId } : {}), ...(q ? { q } : {}) } });
export const storeAllocate = (itemId, data) => flowApi.post(`/store/allocate/${itemId}`, typeof data === 'number' ? { available_qty: data } : data);
export const getPurchaseIndents = (params) => flowApi.get('/purchase-indents', { params });
export const updatePurchaseIndent = (id, data) => flowApi.put(`/purchase-indents/${id}`, data);
export const markIndentReceived = (id) => flowApi.post(`/purchase-indents/${id}/mark-received`);

// Billing
export const getBillingQueue = (companyId, q) => flowApi.get('/billing-queue', { params: { ...(companyId ? { company_id: companyId } : {}), ...(q ? { q } : {}) } });
export const billItem = (itemId, data) => flowApi.post(`/billing/${itemId}`, data);
export const billingShortClose = (itemId, reason) => flowApi.post(`/billing/short-close/${itemId}`, { reason });
export const checkPendingItems = (data) => flowApi.post('/sales-orders/check-pending-items', data);

// Store push-to-logistics (after billing, store packs and pushes for dispatch)
export const getReadyToShipQueue = (companyId, q) => flowApi.get('/ready-to-ship', { params: { ...(companyId ? { company_id: companyId } : {}), ...(q ? { q } : {}) } });
export const pushToLogistics = (itemId) => flowApi.post(`/store/push-logistics/${itemId}`);

// Logistics
export const getLogisticsQueue = (params) => flowApi.get('/logistics-queue', { params });
export const setPurchaseItemAlias = (itemId, name) => flowApi.post(`/purchase/item-alias/${itemId}`, { purchase_item_name: name });
export const dispatchItem = (itemId, data) => flowApi.post(`/logistics/dispatch/${itemId}`, data);
export const markDelivered = (itemId, data) => flowApi.post(`/logistics/delivered/${itemId}`, data);
// Lot-level dispatch / delivery (split shipments)
export const listItemLots = (itemId) => flowApi.get(`/logistics/items/${itemId}/lots`);
export const markLotDelivered = (lotId, data) => flowApi.post(`/logistics/lots/${lotId}/delivered`, data);
export const resendLotEmail = (lotId) => flowApi.post(`/logistics/lots/${lotId}/resend-email`);
export const resendItemEmail = (itemId) => flowApi.post(`/logistics/items/${itemId}/resend-email`);

// Flow extras
export const getFlowMyCustomers = (q) => flowApi.get('/my-customers', { params: q ? { q } : {} });
export const getFlowMyItems = (q) => flowApi.get('/my-items', { params: q ? { q } : {} });
export const getTatReport = (params) => flowApi.get('/tat-report', { params });

// SO edit + approval revert
export const updateSalesOrder = (id, data) => flowApi.put(`/sales-orders/${id}`, data);
export const deleteSalesOrder = (id) => flowApi.delete(`/sales-orders/${id}`);
export const revertItemDecision = (itemId) => flowApi.post(`/sales-order-items/${itemId}/revert`);

// Billing multi-item
export const billMultiItems = (data) => flowApi.post('/billing/multi', data);

// Purchase partial receive + Store verification
export const receivePartial = (indentId, data) => flowApi.post(`/purchase-indents/${indentId}/receive`, data);
export const shortCloseIndent = (indentId, reason) => flowApi.post(`/purchase-indents/${indentId}/short-close`, { reason });
export const getStoreVerifications = (companyId, q) => flowApi.get('/store-verifications', { params: { ...(companyId ? { company_id: companyId } : {}), ...(q ? { q } : {}) } });
export const confirmVerification = (verId, data) => flowApi.post(`/store-verifications/${verId}/confirm`, data);
export const releasePendingAllocation = (itemId, data) => flowApi.post(`/store/release-pending/${itemId}`, data);

// Delivery persons master
export const getDeliveryPersons = () => flowApi.get('/delivery-persons');
export const createDeliveryPerson = (data) => flowApi.post('/delivery-persons', data);
export const deleteDeliveryPerson = (id) => flowApi.delete(`/delivery-persons/${id}`);

// Supplier + communications
export const getMySuppliers = (q) => flowApi.get('/my-suppliers', { params: q ? { q } : {} });
export const getPurchaseCommunications = (indentId) => flowApi.get(`/purchase-indents/${indentId}/communications`);
export const addPurchaseCommunication = (indentId, data) => flowApi.post(`/purchase-indents/${indentId}/communications`, data);
export const setIndentDrawing = (indentId, data) => flowApi.put(`/purchase-indents/${indentId}/drawing`, data);
export const splitPurchaseIndent = (indentId, data) => flowApi.post(`/purchase-indents/${indentId}/split`, data);
export const pushLogisticsByInvoice = (data) => flowApi.post('/store/push-logistics/by-invoice', data);
export const dispatchByInvoice = (data) => flowApi.post('/logistics/dispatch-by-invoice', data);
export const deliverByInvoice = (data) => flowApi.post('/logistics/deliver-by-invoice', data);
export const setStoreRemarks = (itemId, data) => flowApi.post(`/store/item-remarks/${itemId}`, data);
export const getCustomerItemLastPrice = (customer_name, item_name) =>
  flowApi.get('/customer-item-last-price', { params: { customer_name, item_name } });

// SO PDF download
export const downloadSalesOrderPdf = (soId) => flowApi.get(`/sales-orders/${soId}/pdf`, { responseType: 'blob' });

// Mine-scoped dashboard (sales engineer)
export const getMyFlowDashboard = (companyId) => flowApi.get('/dashboard-stats', { params: { mine: true, ...(companyId ? { company_id: companyId } : {}) } });

// Stock Reports (daily Tally snapshot per firm)
export const uploadStockReport = (formData) => flowApi.post('/stock-reports/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const listStockReports = (firmId) => flowApi.get('/stock-reports', { params: firmId ? { firm_id: firmId } : {} });
export const getLatestStockReports = () => flowApi.get('/stock-reports/latest');
export const searchStockItems = (params) => flowApi.get('/stock-reports/items', { params });
export const deleteStockReport = (id) => flowApi.delete(`/stock-reports/${id}`);


// Holidays (admin-managed working-calendar)
export const listHolidays = () => flowApi.get('/holidays');
export const addHoliday = (data) => flowApi.post('/holidays', data);
export const deleteHoliday = (id) => flowApi.delete(`/holidays/${id}`);
export const seedHolidays = () => flowApi.post('/holidays/seed');


// ============= MARKETING MODULE =============
const marketingApi = axios.create({ baseURL: `${API_BASE}/api/tasks-app/marketing` });
marketingApi.interceptors.request.use(cfg => {
  const t = localStorage.getItem('task_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
marketingApi.interceptors.response.use((res) => res, (err) => { handle401(err); return Promise.reject(err); });

// Dashboard
export const mktDashboard = () => marketingApi.get('/dashboard');
// Contacts
export const mktListContacts = (params) => marketingApi.get('/contacts', { params });
export const mktAddContact = (data) => marketingApi.post('/contacts', data);
export const mktUpdateContact = (id, data) => marketingApi.patch(`/contacts/${id}`, data);
export const mktDeleteContact = (id) => marketingApi.delete(`/contacts/${id}`);
export const mktImportContacts = (file) => {
  const fd = new FormData(); fd.append('file', file);
  return marketingApi.post('/contacts/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const mktContactsExportUrl = () => `${API_BASE}/api/tasks-app/marketing/contacts/export`;
export const mktContactsTemplateUrl = () => `${API_BASE}/api/tasks-app/marketing/contacts/template`;
export const mktListTags = () => marketingApi.get('/tags');
// Templates
export const mktListTemplates = () => marketingApi.get('/templates');
// Campaigns
export const mktListCampaigns = (params) => marketingApi.get('/campaigns', { params });
export const mktCreateCampaign = (data) => marketingApi.post('/campaigns', data);
export const mktGetCampaign = (id) => marketingApi.get(`/campaigns/${id}`);
export const mktUpdateCampaign = (id, data) => marketingApi.patch(`/campaigns/${id}`, data);
export const mktDeleteCampaign = (id) => marketingApi.delete(`/campaigns/${id}`);
export const mktPreviewAudience = (id) => marketingApi.post(`/campaigns/${id}/audience-preview`);
export const mktSendTest = (id, to) => marketingApi.post(`/campaigns/${id}/send-test`, { to });
export const mktScheduleCampaign = (id, scheduled_for) => marketingApi.post(`/campaigns/${id}/schedule`, { scheduled_for });
export const mktCancelSchedule = (id) => marketingApi.post(`/campaigns/${id}/cancel-schedule`);
export const mktSendNow = (id) => marketingApi.post(`/campaigns/${id}/send-now`);
export const mktCampaignSends = (id) => marketingApi.get(`/campaigns/${id}/sends`);
// Asset upload
export const mktUpload = (file, kind = 'banner') => {
  const fd = new FormData(); fd.append('file', file);
  return marketingApi.post(`/upload?kind=${encodeURIComponent(kind)}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
// AI

// Branding (master template for all outbound emails)
export const getBranding = () => flowApi.get('/branding');
export const updateBranding = (data) => flowApi.post('/branding', data);
export const uploadBrandLogo = (file) => {
  const fd = new FormData(); fd.append('file', file);
  return flowApi.post('/branding/upload-logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const uploadBrandSocialIcon = (file) => {
  const fd = new FormData(); fd.append('file', file);
  return flowApi.post('/branding/upload-social-icon', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export const mktAIContent = (data) => marketingApi.post('/ai/content', data);
export const mktAIBanner = (prompt) => marketingApi.post('/ai/banner', { prompt });


// Pendency reports + admin revert (Cycle 5)
export const getPendencyByEngineer = (companyId) => flowApi.get('/reports/pendency-by-engineer', { params: companyId ? { company_id: companyId } : {} });
export const getPendencyByCustomer = (companyId) => flowApi.get('/reports/pendency-by-customer', { params: companyId ? { company_id: companyId } : {} });
export const adminRevertItem = (itemId, reason) => flowApi.post(`/admin/revert-item/${itemId}`, { reason });
export const adminUpdateInvoice = (data) => flowApi.post('/admin/update-invoice', data);
export const getShortClosedReport = (companyId) => flowApi.get('/reports/short-closed', { params: companyId ? { company_id: companyId } : {} });
export const exportPendencyExcel = (companyId) => flowApi.get('/reports/pendency/export', { params: companyId ? { company_id: companyId } : {}, responseType: 'blob' });
export const sendFlowNotification = (data) => flowApi.post('/notifications', data);
export const getFlowNotifications = () => flowApi.get('/notifications');
export const markFlowNotificationsRead = (ids) => flowApi.post('/notifications/mark-read', { ids });
export const dismissFlowNotifications = (ids) => flowApi.post('/notifications/dismiss', { ids });
export const getSalesAnalytics = (params) => flowApi.get('/analytics/sales', { params });
export const getAdminReverts = (itemId) => flowApi.get('/admin/reverts', { params: itemId ? { item_id: itemId } : {} });
